import { useEffect, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTab } from '../contexts/TabContext';

// Route configuration for tab labels and icons
const routeConfig: Record<string, { 
  label: string; 
  icon?: React.ReactNode; 
  getLabel?: (params: Record<string, string>) => string;
}> = {
  '/': { label: 'Dashboard' },
  '/warehouse': { label: 'Warehouses' },
  '/warehouse/:id': { 
    label: 'Warehouse Details',
    getLabel: (params) => `Warehouse ${params.id}`
  },
  '/storage-unit': { label: 'Storage Units' },
  '/storage-unit/:id': {
    label: 'Storage Unit Details',
    getLabel: (params) => `Storage Unit ${params.id}`
  },
  '/units': { label: 'Units' },
  '/unknown-units': { label: 'Unknown Units' },
  '/units/:id': {
    label: 'Unit Details',
    getLabel: (params) => `Unit ${params.id}`
  },
  '/analytics/product/:id': {
    label: 'Product Analytics',
    getLabel: (params) => `Analytics ${params.id}`
  },
  '/box/:boxBarcode': {
    label: 'Box Details',
    getLabel: (params) => `Box ${params.boxBarcode}`
  },
  '/movements': { label: 'Movement Dashboard' },
  '/movements/all': { label: 'All Movements' },
  '/movements/reallocation': { label: 'Reallocation' },
  '/movements/move-in': { label: 'Move In' },
  '/movements/move-out': { label: 'Move Out' },
  '/types': { label: 'Product Types' },
  '/suppliers': { label: 'Suppliers' },
  '/clients': { label: 'Clients' },
  '/shipments': { label: 'Shipments' },
  '/shipments/:id': {
    label: 'Shipment Details',
    getLabel: (params) => `Shipment ${params.id}`
  },
  '/audit-dashboard': { label: 'Audit Dashboard' },
  '/users': { label: 'Users' },
};

const findRouteConfig = (pathname: string) => {
  // First try exact match
  if (routeConfig[pathname]) {
    return { key: pathname, config: routeConfig[pathname] };
  }

  // Then try parameterized routes
  for (const [route, config] of Object.entries(routeConfig)) {
    if (route.includes(':')) {
      const pattern = route.replace(/:[^/]+/g, '([^/]+)');
      const regex = new RegExp(`^${pattern}$`);
      const match = pathname.match(regex);
      if (match) {
        return { key: route, config, matches: match.slice(1) };
      }
    }
  }

  return null;
};

export const useTabNavigation = () => {
  const location = useLocation();
  const params = useParams();
  const { addTab, setActiveTab, tabs } = useTab();

  const createTabFromRoute = useCallback((pathname: string, params: Record<string, string> = {}) => {
    const routeMatch = findRouteConfig(pathname);
    
    if (!routeMatch) {
      // Fallback for unknown routes
      const segments = pathname.split('/').filter(Boolean);
      const label = segments[segments.length - 1]?.charAt(0).toUpperCase() + segments[segments.length - 1]?.slice(1) || 'Page';
      return {
        label,
        path: pathname,
        params,
        closable: true
      };
    }

    const { config } = routeMatch;
    let label = config.label;

    // Use custom label generator if available
    if (config.getLabel && Object.keys(params).length > 0) {
      label = config.getLabel(params);
    }

    return {
      label,
      path: pathname,
      icon: config.icon,
      params,
      closable: true
    };
  }, []);

  // Auto-create tab when navigating to a new route
  useEffect(() => {
    const existingTab = tabs.find(tab => tab.path === location.pathname);
    
    if (!existingTab) {
      const tabData = createTabFromRoute(location.pathname, params);
      const tabId = addTab(tabData);
      setActiveTab(tabId);
    } else {
      setActiveTab(existingTab.id);
    }
  }, [location.pathname, params, tabs, addTab, setActiveTab, createTabFromRoute]);

  return {
    createTabFromRoute
  };
};